/**
 * Copyright 2025 SweetCode. All rights reserved.
 *
 * OpenAI browser pixel functions (free tier)
 *
 * Everything the OpenAI measurement pixel needs to load and fire browser
 * events. The Conversions API helpers (list-shaped user data, obref, oppref,
 * base payload) live in functions_premium.js.
 *
 * https://developers.openai.com/ads/measurement-pixel
 * */

(function (pmw, undefined) {

	pmw.getOpenAIPixelId = () => {
		return pmwDataLayer.pixels.openai.pixel_id;
	};

	/**
	 * User matching data for the browser pixel (advanced matching).
	 *
	 * OpenAI user data is request-scoped: it is passed in the oaiq("init", ...)
	 * call, not in individual oaiq("measure", ...) calls. The pixel takes the
	 * singular field names; hashes are lowercase hex SHA-256 of the normalized
	 * value (computed server-side into the data layer), geographic values are
	 * raw.
	 *
	 * Advanced Matching is a Pro setting and the hashed user data is only in
	 * the data layer on Pro, so this returns an empty object on free.
	 * https://developers.openai.com/ads/measurement-pixel
	 *
	 * @return {object}
	 */
	pmw.getOpenAIUserData = () => {

		const user = {};

		if (!pmwDataLayer?.pixels?.openai?.advanced_matching) return user;

		const dl = pmwDataLayer?.user;

		if (dl?.email?.sha256) user.email_sha256 = dl.email.sha256;
		if (dl?.phone?.openai) user.phone_number_sha256 = dl.phone.openai;
		if (dl?.id?.sha256) user.external_id_sha256 = dl.id.sha256;
		if (dl?.first_name?.openai) user.first_name_sha256 = dl.first_name.openai;
		if (dl?.last_name?.openai) user.last_name_sha256 = dl.last_name.openai;
		if (dl?.country?.raw) user.country = String(dl.country.raw).toUpperCase();
		if (dl?.city?.raw) user.city = dl.city.raw;
		if (dl?.state?.openai || dl?.state?.raw) user.region = dl.state.openai || dl.state.raw;
		if (dl?.postcode?.raw) user.postal_code = dl.postcode.raw;

		return user;
	};

	pmw.loadOpenAIPixel = () => {

		try {
			// @formatter:off
			(function (w, d, s, u) {
				if (w.oaiq) return;
				var q = function () { q.q.push(arguments); };
				q.q = [];
				w.oaiq = q;
				var js = d.createElement(s);
				js.async = true;
				js.src = u;
				var f = d.getElementsByTagName(s)[0];
				f.parentNode.insertBefore(js, f);
			})(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
			// @formatter:on

			const initConfig = { pixelId: pmw.getOpenAIPixelId() };

			// User data is request-scoped and belongs in the init call.
			const userData = pmw.getOpenAIUserData();
			if (Object.keys(userData).length > 0) {
				initConfig.user = userData;
			}

			// The pixel is only loaded once marketing consent is granted, but the
			// SDK remembers a denial (see the pmw:consent:update listener) across
			// page loads and would keep dropping events after the visitor changes
			// their mind. Clear a stored denial before initializing.
			oaiq("consent", true);

			oaiq("init", initConfig);

			pmwDataLayer.pixels.openai.loaded = true;

			pmw.console.log("OpenAI Pixel: pixel loaded");

		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * ISO 4217 minor-unit exponent for a currency (default 2).
	 *
	 * @param {string} currency
	 * @return {number}
	 */
	pmw.openaiCurrencyExponent = (currency) => {
		const zeroDecimal  = [ "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF" ];
		const threeDecimal = [ "BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND" ];
		const c            = (currency || "").toUpperCase();

		if (zeroDecimal.indexOf(c) !== -1) return 0;
		if (threeDecimal.indexOf(c) !== -1) return 3;
		return 2;
	};

	/**
	 * Convert a decimal amount into integer ISO 4217 minor units
	 * (e.g. 129.99 USD -> 12999). The browser pixel and the Conversions API
	 * both report amounts this way.
	 *
	 * @param {number} amount
	 * @param {string} currency
	 * @return {number}
	 */
	pmw.openaiToMinorUnits = (amount, currency) => {
		const exponent = pmw.openaiCurrencyExponent(currency);
		return Math.round((Number(amount) || 0) * Math.pow(10, exponent));
	};

	/**
	 * Can fire OpenAI event
	 * @return {boolean}
	 */
	pmw.canFireOpenAIEvent = () => {
		if (!pmwDataLayer?.pixels?.openai?.loaded) return false;

		if (!pmw.consent.categories.get().marketing) return false;

		return true;
	};

	/**
	 * Can not fire OpenAI event
	 * @return {boolean}
	 */
	pmw.canNotFireOpenAIEvent = () => {
		return !pmw.canFireOpenAIEvent();
	};

}(window.pmw = window.pmw || {}));
